import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import Clipper from '@components/Clipper';
import type { ClipperProps } from '@components/Clipper';
import Comments from '@components/Comments';
import VerticalAdvertisement from '@components/FeaturedPartnerAd/Vertical';
import MostPopularArticles from '@components/MostPopularArticles';
import RelatedStories from '@components/RelatedStories';
import type { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import type { Env } from '@contexts/AppContext';
import useServerSideRendering from '@hooks/useServerSideRendering';
import type { Article, MostPopularArticle, Pickup, ScriptList } from '@interfaces/homepage';
import { TrackingLocation } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';

import ArticlePickups from '../ArticlePickups';
import Body from '../Body';
import EndOfArticleDeepResearch from '../EndOfArticle/DeepResearch';
import { MidArticleUnitTypes, type SuggestedAiPrompt } from '../interfaces';

interface ContentProps {
  addendum: Article['addendum'];
  advertisement?: Article['advertisement'];
  audioActive?: boolean;
  authors: Article['authors'];
  bodyClassName?: string;
  canViewFullArticle: boolean;
  canViewComments: boolean;
  clipperProps: ClipperProps;
  commentsCount?: number;
  endOfArticleDeepResearchPrompts?: string[];
  env: Env;
  freeBlurb: Article['freeBlurb'];
  featuredGraphic?: Article['featuredGraphic'];
  fullText: Article['fullText'];
  highlight?: string;
  id: number;
  isSEOBotAndArticleIsNotFree: boolean;
  midArticleUnitType?: MidArticleUnitTypes;
  mostPopularArticles: MostPopularArticle[];
  newsletterId?: string | number;
  pickups?: Pickup[];
  relatedArticles: Article[];
  relatedEvent?: MidArticleEventProps;
  scriptList?: ScriptList[];
  suggestedAiPrompts?: SuggestedAiPrompt[];
  takeaway: Article['takeaway'];
}

const Content = ({
  addendum,
  advertisement,
  audioActive,
  authors,
  bodyClassName,
  canViewFullArticle,
  canViewComments,
  clipperProps,
  commentsCount,
  endOfArticleDeepResearchPrompts,
  env,
  freeBlurb,
  featuredGraphic,
  fullText,
  highlight,
  id,
  isSEOBotAndArticleIsNotFree,
  midArticleUnitType,
  mostPopularArticles,
  newsletterId,
  pickups,
  relatedArticles,
  relatedEvent,
  scriptList,
  takeaway,
}: ContentProps): JSX.Element => {
  const hydrated = useServerSideRendering();

  return (
    <>
      <div
        className={clsx('flex flex-col md:flex-row md:gap-72 xl:gap-112', {
          'mb-80': !canViewFullArticle,
        })}
      >
        <div className="flex flex-1 flex-col">
          {featuredGraphic && canViewFullArticle && (
            <div
              className={clsx({ 'seo-paywall-content': isSEOBotAndArticleIsNotFree })}
              data-nosnippet
            >
              {ReactHtmlParser(featuredGraphic)}
            </div>
          )}
          <div className="md:ml-[60px] xl:ml-[84px]">
            <Body
              addendum={addendum}
              authors={authors}
              canViewFullArticle={canViewFullArticle}
              className={bodyClassName}
              freeBlurb={freeBlurb}
              fullText={fullText}
              highlight={highlight}
              isSEOBotAndArticleIsNotFree={isSEOBotAndArticleIsNotFree}
              newsletterId={newsletterId}
              relatedArticles={relatedArticles}
              relatedEvent={relatedEvent}
              scriptList={scriptList}
              takeaway={takeaway}
              audioActive={audioActive}
            />
            {canViewFullArticle && endOfArticleDeepResearchPrompts && (
              <EndOfArticleDeepResearch id={id} prompts={endOfArticleDeepResearchPrompts} />
            )}
            {canViewFullArticle && pickups?.length > 0 && <ArticlePickups pickups={pickups} />}
          </div>
        </div>
        <div className="flex-shrink-0 print:hidden md:w-[195px] lg:w-[260px]">
          {mostPopularArticles.length > 0 && canViewFullArticle && (
            <MostPopularArticles
              className="mt-[260px] hidden md:block lg:pr-[30px] xl:mt-[218px]"
              mostPopularArticles={mostPopularArticles}
            />
          )}
          {advertisement && (
            <VerticalAdvertisement
              advertisement={advertisement}
              className="mt-64 hidden md:block"
              trackingLocation={TrackingLocation.SideFeed}
            />
          )}
        </div>
      </div>
      {hydrated && canViewComments && (
        <div className="mr-0 mt-80 print:hidden sm:mt-112 md:mr-[267px] lg:mr-[372px]">
          <Comments
            commentableId={id}
            commentableType={CommentableType.Article}
            commentsCount={commentsCount}
          />
        </div>
      )}
      {canViewFullArticle && advertisement && (
        <div className="mx-48">
          <VerticalAdvertisement
            advertisement={advertisement}
            className="mt-48 block md:hidden"
            trackingLocation={TrackingLocation.ContentBody}
          />
        </div>
      )}
      {(!midArticleUnitType ||
        (midArticleUnitType === MidArticleUnitTypes.Event && relatedEvent) ||
        midArticleUnitType === MidArticleUnitTypes.DeepResearch) && (
        <RelatedStories articles={relatedArticles} className="my-80 print:hidden sm:my-112" />
      )}
      {hydrated && <Clipper {...clipperProps} env={env} />}
    </>
  );
};

export default Content;
